import React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Alert, Layout, Button, Row, Col, DatePicker, Radio, message } from 'antd';
import { connect } from 'react-redux';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { withRouter } from 'react-router';
import {
	nullSafeGetOrElse,
	nullSafeGet,
	getCurrency,
	compareStrings,
} from '../../utils/DataAccessUtils';
import BackButton from '../back_button/BackButton';
import { debounce } from '../../utils/PerformanceUtils';
import {
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
} from '../../thunks/work_orders_thunks';
import {
	createInvoiceByInternalTech,
	fetchTaxLineItemsForBuyer,
	fetchTaxLineItemsForSupplier,
	getInvoiceNumber,
	getInvoiceNumberBuyer,
	invoicesRestCrudThunksForBuyer,
	invoicesRestCrudThunksForSupplier,
} from '../../thunks/invoices_thunks';
import moment from 'moment';
import {
	proposalsRestCrudThunksForBuyer,
	proposalsRestCrudThunksForSupplier,
} from '../../thunks/proposals_thunks';
import FormDots from '../common/FormDots';
import { ROLE_TYPES } from '../../utils/DataConstants';
import OWRichTextEditor from '../../rich_text_editor/OWRichTextEditor';
import {
	isAllowedToPerform,
	isSupplierAllowedToAccess,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';
import { getInvoiceApprovalHierarchiesByAttributes } from '../../thunks/company_approval_thunks';
import { isUserAllowedToFullyApprove } from '../../utils/ApprovalHierarchyUtils';
import {
	getInvoiceDueDateBasedOnPaymentTerms,
	getInvoiceEntityCommonValues,
	initialiseFormDataWithLineItems,
	initializeInvoiceTotalFields,
	initializeLineItemKeys,
	LINE_ITEM_FIELD,
	mergeProposalIntoFormData,
	recalculateInvoiceTotalFields,
} from '../../utils/InvoiceUtils';
import ClearErrors from '../clear_errors/ClearErrors';
import { INVOICES_CRUD_ACTION_CREATORS } from '../../actions/invoices_actions';
import InvoicePaymentTermComponent from './InvoicePaymentTermComponent';
import LaborFormFields from './LaborFormFields';
import PartFormFields from './PartFormFields';
import EquipmentFormFields from './EquipmentFormFields';
import TaxFormFields from './TaxFormFields';
import { TIME_IN } from '../supplier_invoice_form/LaborTimeSelect';
import SimpleLineItemFormFields from './SimpleLineItemFormFields';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';

require('./BuyerInvoiceForm.less');

interface BuyerInvoiceFormProps extends FormComponentProps {
	formData: any;
	onSuccess: any;
	creating?: boolean;
	updating?: boolean;
	createErrors?: string[];
	updateErrors?: string[];
	redirectForwardUrl?: string;
	redirectBackwardUrl?: string;
	currentUser?: any;
	history?: any;
	createInvoice?: any;
	updateInvoice?: any;
	workOrder?: any;
	getWorkOrder?: any;
	getQuotes?: any;
	checkInvoiceNumber?: any;
	fetchTaxLineItems?: any;
	userType?: any;
	companyConfig: any;
	getApprovalHierarchy: any;
}

let partUuid = 1;
let laborUuid = 1;
let miscUuid = 1;
let equipmentUuid = 1;
let travelUuid = 1;
let freightUuid = 1;
class BuyerInvoiceForm extends React.Component<BuyerInvoiceFormProps, any> {
	constructor(props) {
		super(props);
		this.state = {
			formDataInitialized: false,
			isUpdate: props.formData.hasOwnProperty('id') && props.formData.id !== undefined,
			laborItemInitialValues: {},
			partItemInitialValues: {},
			equipmentItemInitialValues: {},
			miscItemInitialValues: {},
			hasQuote: false,
			editorRef: React.createRef(),
			timeIn: TIME_IN.HOURS,
			formData: {},
		};
	}

	setValuesInState = (val) => this.setState(val);

	initializeFormWithData = (mergedFormData) => {
		const {
			labor,
			part,
			equipment,
			misc,
			travel,
			freight,
			formData: newFormData,
		} = initialiseFormDataWithLineItems(mergedFormData, this.state.timeIn);
		laborUuid = labor;
		partUuid = part;
		equipmentUuid = equipment;
		miscUuid = misc;
		travelUuid = travel;
		freightUuid = freight;

		this.setState({
			formDataInitialized: true,
			formData: { ...mergedFormData, ...newFormData },
		});
	};

	recalculateTotals = debounce(() => {
		const { form } = this.props;
		recalculateInvoiceTotalFields(form);
	}, 200);

	componentDidMount() {
		const { form, getWorkOrder, formData, getQuotes, fetchTaxLineItems, getApprovalHierarchy } =
			this.props;
		const { isUpdate } = this.state;

		this.setState({ formDataInitialized: false });

		if (!formData.workOrderId) {
			this.initializeFormWithData(formData);
			getApprovalHierarchy()
				.then((data) => {
					this.setState({ approvalHierarchy: data });
				})
				.catch((err) => {
					console.error('Error loading approval hierarchy:', err);
				});
			return;
		}

		getWorkOrder(formData.workOrderId)
			.then((fetchedWorkOrder) => {
				let mergedFormData = { ...formData };
				let hasQuote = false;

				if (!isUpdate) {
					return getQuotes(
						{
							workOrderId: formData.workOrderId,
							buyerFacilityId: fetchedWorkOrder.buyerFacilityId,
						},
						'workOrderAssociatedQuotes'
					).then((proposals) => {
						if (proposals && proposals.length > 0) {
							hasQuote = true;
							const awardedProposals = proposals.filter(
								(proposal) => proposal.status && proposal.status.toLowerCase() === 'awarded'
							);
							if (awardedProposals.length === 1) {
								mergedFormData = mergeProposalIntoFormData(formData, awardedProposals[0]);
							}
						}
						return { mergedFormData, hasQuote, fetchedWorkOrder };
					});
				}

				return { mergedFormData, hasQuote, fetchedWorkOrder };
			})
			.then(({ mergedFormData, hasQuote, fetchedWorkOrder }) => {
				this.initializeFormWithData(mergedFormData);

				if (hasQuote) {
					this.setState({ hasQuote: true });
				}

				const currency = hasQuote
					? getCurrency({ workOrder: fetchedWorkOrder })
					: getCurrency({ supplier: nullSafeGet('supplierFacility', fetchedWorkOrder) });
				if (currency && currency.id === 'CAD') {
					fetchTaxLineItems(currency.id)
						.then((data) => {
							if (data) {
								form.setFieldsValue({ taxKeys: data });
							}
						})
						.catch((err) => {
							console.error('Error fetching tax line items:', err);
						});
				}

				getApprovalHierarchy()
					.then((data) => {
						this.setState({ approvalHierarchy: data });
					})
					.catch((err) => {
						console.error('Error loading approval hierarchy:', err);
					});
			})
			.catch((err) => {
				console.error('Error loading form data:', err);
				this.initializeFormWithData(formData);
				getApprovalHierarchy()
					.then((data) => {
						this.setState({ approvalHierarchy: data });
					})
					.catch((hierarchyErr) => {
						console.error('Error loading approval hierarchy:', hierarchyErr);
					});
			});
	}

	getScope = () =>
		nullSafeGet('state.editorRef.current', this) &&
		(this.state.editorRef.current as any).getContent();

	handleSubmit = (e) => {
		const {
			form,
			formData,
			redirectForwardUrl,
			currentUser,
			history,
			onSuccess,
			createInvoice,
			updateInvoice,
		} = this.props;
		e.preventDefault();

		form.validateFields((err, values) => {
			if (!err) {
				const submitAction = values.id ? updateInvoice : createInvoice;

				const invoice = {
					...getInvoiceEntityCommonValues(
						values,
						formData,
						this.getScope(),
						currentUser,
						this.state.timeIn
					),
					status: values.status || 'pending',
					invoicePDFLink: formData.invoicePDFLink,
					isDeleted: formData.isDeleted,
					buyerFacilityId: values.buyerFacilityId || nullSafeGet('facility.id', currentUser),
					supplierFacilityId: formData.supplierFacilityId,
				};

				submitAction(invoice)
					.then((record) => {
						if (redirectForwardUrl) {
							history.push(redirectForwardUrl);
						}
						if (onSuccess) {
							onSuccess(record);
						}
					})
					.catch((err) => {
						if (err && err.startsWith('Invoice(s) are already associated with this work order')) {
							message.error(err);
							history.goBack();
						}
					});
			}
		});
	};

	handleInvoiceNumberChange = async (rule, value, callback) => {
		const { checkInvoiceNumber, formData, userType, workOrder } = this.props;
		const { isUpdate } = this.state;
		if (value) {
			try {
				let data = await checkInvoiceNumber(value, nullSafeGet('supplierFacilityId', workOrder));
				if (data && Array.isArray(data)) {
					let hasDuplicate = false;
					data.forEach((invoice) => {
						if (
							(isUpdate && nullSafeGet('id', invoice) !== nullSafeGet('id', formData)) ||
							!isUpdate
						) {
							hasDuplicate = true;
							callback(
								<span>
									Error: Duplicate with{' '}
									<a
										href={`/${userType}/invoices/detail/`.concat(
											nullSafeGet('id', invoice),
											`/details`
										)}
										target="_blank"
										rel="noreferrer"
									>
										Invoice {nullSafeGet('id', invoice)}
									</a>
								</span>
							);
						}
					});
					if (!hasDuplicate) {
						callback();
					}
				} else {
					callback();
				}
			} catch (error) {
				callback();
			}
		} else {
			callback();
		}
	};

	onPaymentTermSelect = (val) => {
		const { form } = this.props;
		const { setFieldsValue, getFieldValue } = form;

		const dueDate = getInvoiceDueDateBasedOnPaymentTerms(getFieldValue, val);

		setFieldsValue({
			paymentTerms: val,
			...(dueDate && { dueDate }),
		});
	};

	render() {
		const {
			creating,
			updating,
			updateErrors,
			createErrors,
			form,
			workOrder,
			companyConfig,
			currentUser,
			userType,
		} = this.props;
		const { formDataInitialized, formData, isUpdate, hasQuote, approvalHierarchy } = this.state;
		if (!formDataInitialized) {
			return <PageLoadingPlaceholder />;
		}
		const submitting = creating || updating;
		const errorMessagesText = createErrors.concat(updateErrors).join('; ');
		let submitText = isUpdate ? 'Update invoice' : 'Create invoice';
		const { getFieldDecorator, getFieldValue } = form;
		const currency = !!hasQuote
			? getCurrency({ workOrder })
			: getCurrency({ supplier: nullSafeGet('supplierFacility', workOrder) });

		const DATE_FORMAT = 'MM-DD-YYYY';
		const allowedToMarkProcessingOrPaid =
			userType === ROLE_TYPES.SUPPLIER
				? isSupplierAllowedToAccess(
						PERMISSION_NAMES.CAN_MARK_INVOICES_PROCESSING_OR_PAID,
						companyConfig,
						currentUser.roles
				  )
				: isAllowedToPerform(
						PERMISSION_NAMES.CAN_MARK_INVOICES_PROCESSING_OR_PAID,
						companyConfig,
						currentUser.roles
				  );
		const buyerCompanyCurrency = getCurrency({
			buyerCompany: nullSafeGet('workOrder.buyerCompany', workOrder),
		});
		const exchangeRate =
			currency.id === buyerCompanyCurrency.id
				? 1
				: nullSafeGetOrElse('currencyExchangeRateLocationToSupplier', workOrder, 1) *
				  nullSafeGetOrElse('currencyExchangeRateBuyerToLocation', workOrder, 1);
		const canFullyApprove = isUserAllowedToFullyApprove(
			currentUser,
			approvalHierarchy,
			companyConfig,
			getFieldValue('invoiceTotalAfterTax') / exchangeRate
		);

		getFieldDecorator('id', { initialValue: formData.id });
		getFieldDecorator('attachments', { initialValue: formData.attachments || [] });

		initializeLineItemKeys(formData, getFieldDecorator);
		initializeInvoiceTotalFields(formData, getFieldDecorator);

		//TODO: UPGRADE FORM
		return (
			<Layout.Content>
				<Row className="buyerInvoiceForm" gutter={16}>
					<Col span={24}>
						<Form hideRequiredMark={true} layout="vertical">
							<ClearErrors actionCreators={INVOICES_CRUD_ACTION_CREATORS} />
							{createErrors.length > 0 || updateErrors.length > 0 ? (
								<Form.Item label="">
									<Alert message={errorMessagesText} type="error" />
								</Form.Item>
							) : null}
							<div style={{ marginBottom: 32 }}>
								<div className="buyerInvoiceForm__sectionHeadPrimary">Scope</div>
								{getFieldDecorator('scope', {
									initialValue: nullSafeGet(`scope`, formData),
								})(
									<OWRichTextEditor
										initialEditorState={nullSafeGet(`scope`, formData)}
										editorRef={this.state.editorRef}
									/>
								)}
							</div>
							<Form.Item className="buyerInvoiceForm__sectionHeadPrimary" label="Invoice #">
								{getFieldDecorator('invoiceNumber', {
									validateTrigger: ['onBlur'],
									initialValue: nullSafeGet(`invoiceNumber`, formData),
									rules: [
										{
											whitespace: true,
											required: true,
											message: 'This field is required.',
										},
										{
											validator: this.handleInvoiceNumberChange,
										},
									],
								})(<Input style={{ maxWidth: 320 }} />)}
							</Form.Item>
							<div style={{ marginBottom: 32 }}>
								<div className="buyerInvoiceForm__sectionHeadPrimary">Work Order #</div>
								{getFieldDecorator('externalWorkOrderId', {
									initialValue: nullSafeGetOrElse(
										`externalWorkOrderId`,
										formData,
										workOrder && workOrder.id ? `${workOrder.id}` : ''
									),
								})(<Input style={{ maxWidth: 320 }} />)}
							</div>
							<Form.Item className="buyerInvoiceForm__sectionHeadPrimary" label="Invoice Date">
								{getFieldDecorator('dateOfInvoice', {
									initialValue: formData.dateOfInvoice ? moment(formData.dateOfInvoice) : undefined,
									rules: [
										{
											required: true,
											message: 'Please specify invoice date.',
										},
									],
								})(<DatePicker format={DATE_FORMAT} />)}
							</Form.Item>
							<Form.Item className="buyerInvoiceForm__sectionHeadPrimary" label="Payment Terms">
								{getFieldDecorator('paymentTerms', {
									initialValue: nullSafeGet('paymentTerms', formData),
								})(<Input placeholder="Net 30" style={{ maxWidth: 320 }} />)}
								<InvoicePaymentTermComponent onSelect={this.onPaymentTermSelect} />
							</Form.Item>
							<div style={{ marginBottom: 32 }}>
								<div className="buyerInvoiceForm__sectionHeadPrimary">Due Date</div>
								{getFieldDecorator('dueDate', {
									initialValue: formData.dueDate ? moment(formData.dueDate) : undefined,
								})(<DatePicker format={DATE_FORMAT} />)}
							</div>
							<Form.Item
								className="buyerInvoiceForm__sectionHeadPrimary"
								label="Has this invoice been paid?"
								hidden={!allowedToMarkProcessingOrPaid}
							>
								{getFieldDecorator('status', {
									validateTrigger: ['onBlur'],
									initialValue: 'pending',
								})(
									<Radio.Group>
										<Radio.Button
											value={'pending'}
											style={{ flexWrap: 'nowrap', height: 40, lineHeight: '40px' }}
										>
											No
										</Radio.Button>
										<Radio.Button
											value={'paid'}
											style={{ flexWrap: 'nowrap', height: 40, lineHeight: '40px' }}
										>
											Yes
										</Radio.Button>
									</Radio.Group>
								)}
							</Form.Item>
							<LaborFormFields
								formData={formData}
								form={form}
								currency={currency}
								initialLaborUuid={laborUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<PartFormFields
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={partUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<EquipmentFormFields
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={equipmentUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<SimpleLineItemFormFields
								fieldName={LINE_ITEM_FIELD.TRAVEL}
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={travelUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<SimpleLineItemFormFields
								fieldName={LINE_ITEM_FIELD.FREIGHT}
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={freightUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<SimpleLineItemFormFields
								fieldName={LINE_ITEM_FIELD.MISC}
								formData={formData}
								form={form}
								currency={currency}
								initialUuid={miscUuid}
								state={this.state}
								setState={this.setValuesInState}
								recalculateTotals={this.recalculateTotals}
							/>
							<div style={{ marginBottom: 32 }}>
								<div className="buyerInvoiceForm__sectionHead">
									<div className="buyerInvoiceForm__sectionHeadPrimary">Total Before Tax</div>
									<FormDots />
									<div className="buyerInvoiceForm__sectionHeadSecondary">
										{currency.format(getFieldValue('invoiceTotalBeforeTax'))}
									</div>
								</div>
							</div>
							<TaxFormFields
								formData={formData}
								form={form}
								currency={currency}
								state={this.state}
								recalculateTotals={this.recalculateTotals}
							/>
							<div style={{ marginBottom: 32 }}>
								<div className="buyerInvoiceForm__sectionHead">
									<div className="buyerInvoiceForm__sectionHeadPrimary">Total With Tax</div>
									<FormDots />
									<div className="buyerInvoiceForm__sectionHeadSecondary">
										{currency.format(getFieldValue('invoiceTotalAfterTax'))}
									</div>
								</div>
							</div>
							<Form.Item
								label=""
								help={
									!canFullyApprove && compareStrings(getFieldValue('status'), 'paid')
										? 'You are not allowed to submit this invoice with a paid status for this amount.'
										: null
								}
							>
								<span className="mr-4">
									<BackButton buttonText="Cancel" />
								</span>
								<Button
									loading={submitting}
									type="primary"
									onClick={(e) => this.handleSubmit(e)}
									disabled={!canFullyApprove && compareStrings(getFieldValue('status'), 'paid')}
								>
									{submitText}
								</Button>
							</Form.Item>
						</Form>
					</Col>
				</Row>
			</Layout.Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	formData: ownProps.formData,
	onSuccess: ownProps.onSuccess,
	updating: state.invoices.updating,
	creating: state.invoices.creating,
	createErrors: state.invoices.createErrors,
	updateErrors: state.invoices.updateErrors,
	redirectForwardUrl: ownProps.redirectForwardUrl,
	redirectBackwardUrl: ownProps.redirectBackwardUrl,
	currentUser: state.session.currentUser,
	history: ownProps.history,
	location: ownProps.location,
	backToWorkOrder: ownProps.backToWorkOrder,
	workOrder: state.work_orders.detail,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchTaxLineItems: (currencyId) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchTaxLineItemsForSupplier(currencyId)
				: fetchTaxLineItemsForBuyer(currencyId)
		),
	checkInvoiceNumber: (invoice, supplierFacilityId) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? getInvoiceNumber(invoice, supplierFacilityId)
				: getInvoiceNumberBuyer(invoice, supplierFacilityId)
		),
	createInvoice: (invoice) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? createInvoiceByInternalTech(invoice)
				: invoicesRestCrudThunksForBuyer.create(invoice)
		),
	updateInvoice: (invoice) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? invoicesRestCrudThunksForSupplier.update(invoice)
				: invoicesRestCrudThunksForBuyer.update(invoice)
		),
	getWorkOrder: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrdersRestCrudThunksForSupplier.readOne(id)
				: workOrdersRestCrudThunksForBuyer.readOne(id)
		),
	getQuotes: (params, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? proposalsRestCrudThunksForSupplier.read(params, targetCollectionName)
				: proposalsRestCrudThunksForBuyer.read(params, targetCollectionName)
		),
	getApprovalHierarchy: (params) =>
		dispatch(getInvoiceApprovalHierarchiesByAttributes(ownProps.userType, params)),
});

const componentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(BuyerInvoiceForm)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(Form.create<BuyerInvoiceFormProps>()(componentWithoutUserType));
